import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  ClipboardList,
  Users,
  FileText,
  Receipt,
  BarChart3,
  Settings,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Activity,
  BellRing,
  Zap,
  RotateCcw,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import type { Capability } from '../../config/capabilities';
import { ToastProvider } from '../../contexts/ToastContext';
import { BrandingProvider, useBranding } from '../../contexts/BrandingContext';
import AdminLanguageSwitcher from './AdminLanguageSwitcher';

const INTER_FONT_HREF = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';

const useInterFont = () => {
  useEffect(() => {
    if (document.querySelector(`link[href="${INTER_FONT_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = INTER_FONT_HREF;
    document.head.appendChild(link);
  }, []);
};

const BrandIdentity: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  const { practice_name, logo_url } = useBranding();
  return (
    <Link
      to="/admin"
      onClick={onClick}
      className="flex items-center gap-2.5 group focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/50 rounded-md min-w-0"
      aria-label={`${practice_name} — Dashboard`}
    >
      {logo_url && (
        <img
          src={logo_url}
          alt=""
          className="h-9 w-9 object-contain transition-transform duration-300 ease-out group-hover:scale-[1.03] drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
        />
      )}
      <span
        className={`text-white font-semibold tracking-tight truncate ${
          logo_url ? 'text-sm max-w-[140px]' : 'text-base max-w-[200px]'
        }`}
      >
        {practice_name}
      </span>
    </Link>
  );
};

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  current: boolean;
  // Bereichsrecht, das diesen Punkt sichtbar macht. Ohne cap = immer sichtbar
  // (Dashboard, Hilfe). adminOnly = nur für Admins (Einstellungen).
  cap?: Capability;
  adminOnly?: boolean;
}

// Hervorgehobener CTA für die Schnellerfassung — bewusst auffälliger als die
// übrige Navigation, damit die Maske für weniger technikaffine Behandler sofort
// auffindbar ist.
const QuickActionLink: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <Link
      to="/admin/quick-appointment"
      onClick={onClick}
      className="group flex items-center gap-2.5 rounded-lg bg-brand-primary px-3 py-2.5 text-[13px] font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-brand-primary-hover cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/50"
    >
      <Zap className="h-[18px] w-[18px] shrink-0" strokeWidth={2.25} aria-hidden />
      <span className="truncate">{t('quick.nav')}</span>
    </Link>
  );
};

const NavLink: React.FC<{ item: NavItem; onClick?: () => void }> = ({ item, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={onClick}
      aria-current={item.current ? 'page' : undefined}
      className={`group relative flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md transition-colors duration-200 cursor-pointer ${
        item.current
          ? 'bg-brand-accent/15 text-brand-primary'
          : 'text-brand-text-muted hover:bg-brand-surface-2 hover:text-brand-primary'
      }`}
    >
      <span
        aria-hidden
        className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full bg-brand-accent transition-all duration-200 ${
          item.current ? 'h-7 opacity-100' : 'h-0 opacity-0 group-hover:h-4 group-hover:opacity-60'
        }`}
      />
      <Icon
        aria-hidden
        className={`h-[18px] w-[18px] shrink-0 transition-colors duration-200 ${
          item.current ? 'text-brand-primary' : 'text-brand-text-muted group-hover:text-brand-primary'
        }`}
        strokeWidth={1.75}
      />
      <span className="truncate flex-1">{item.name}</span>
    </Link>
  );
};

const AdminLayoutInner: React.FC = () => {
  useInterFont();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, can } = useAuth();
  const { practice_name, header_tagline } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const allNavigation: NavItem[] = [
    { name: t('nav.dashboard'), href: '/admin', icon: LayoutDashboard, current: location.pathname === '/admin' },
    { name: t('nav.calendar'), href: '/admin/calendar', icon: Calendar, current: location.pathname === '/admin/calendar', cap: 'calendar' },
    { name: t('nav.appointments'), href: '/admin/appointments', icon: ClipboardList, current: location.pathname === '/admin/appointments', cap: 'appointments' },
    { name: t('nav.reminders'), href: '/admin/reminders', icon: BellRing, current: location.pathname === '/admin/reminders', cap: 'reminders' },
    { name: t('nav.recalls'), href: '/admin/recalls', icon: RotateCcw, current: location.pathname === '/admin/recalls', cap: 'recalls' },
    { name: t('nav.patients'), href: '/admin/patients', icon: Users, current: location.pathname.startsWith('/admin/patients'), cap: 'patients' },
    { name: t('nav.intake'), href: '/admin/intake', icon: FileText, current: location.pathname === '/admin/intake', cap: 'intake' },
    { name: t('nav.invoices'), href: '/admin/invoices', icon: Receipt, current: location.pathname.startsWith('/admin/invoices'), cap: 'invoices' },
    { name: t('nav.reports'), href: '/admin/reports', icon: BarChart3, current: location.pathname === '/admin/reports', cap: 'reports' },
    { name: t('nav.eventLog'), href: '/admin/event-log', icon: Activity, current: location.pathname === '/admin/event-log', cap: 'event-log' },
    { name: t('nav.settings'), href: '/admin/settings', icon: Settings, current: location.pathname === '/admin/settings', adminOnly: true },
    { name: t('nav.help'), href: '/admin/help', icon: HelpCircle, current: location.pathname === '/admin/help' },
  ];

  // Sichtbarkeit: Punkte ohne cap/adminOnly sind immer sichtbar; adminOnly nur
  // für Admins; cap-Punkte nur, wenn der Benutzer das Bereichsrecht hat (can()
  // liefert für Admins ohnehin true).
  const navigation = allNavigation.filter((item) => {
    if (item.adminOnly) return user?.role === 'admin';
    if (item.cap) return can(item.cap);
    return true;
  });

  // Schnellerfassung gehört zum Bereich "appointments".
  const canQuickAppointment = can('appointments');

  return (
    <div data-admin-shell className="bg-brand-surface-2 min-h-screen font-admin text-[13px] text-brand-text">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)}></div>
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-brand-surface">
          <div className="relative flex items-center justify-between h-16 px-4 bg-gradient-to-br from-brand-primary to-brand-primary-hover shadow-md after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-brand-accent/40 after:to-transparent">
            <BrandIdentity onClick={() => setSidebarOpen(false)} />
            <button
              onClick={() => setSidebarOpen(false)}
              aria-label={t('nav.closeSidebar')}
              className="text-white/85 hover:text-white transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-md p-1"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
          <nav className="flex-1 px-3 py-4">
            {canQuickAppointment && (
              <div className="mb-3">
                <QuickActionLink onClick={() => setSidebarOpen(false)} />
              </div>
            )}
            <ul className="space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <NavLink item={item} onClick={() => setSidebarOpen(false)} />
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-brand-surface border-r border-brand-border">
          <div className="relative flex items-center h-16 px-4 bg-gradient-to-br from-brand-primary to-brand-primary-hover shadow-md after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-brand-accent/40 after:to-transparent">
            <BrandIdentity />
          </div>
          <nav className="flex-1 px-3 py-4">
            {canQuickAppointment && (
              <div className="mb-3">
                <QuickActionLink />
              </div>
            )}
            <ul className="space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <NavLink item={item} />
                </li>
              ))}
            </ul>
          </nav>
          <div className="px-4 py-3 border-t border-brand-border">
            <p className="text-[10px] uppercase tracking-[0.15em] text-brand-text-muted font-semibold">{practice_name} · v1.0</p>
          </div>
        </div>
      </div>

      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top header */}
        <div className="sticky top-0 z-30 bg-brand-surface shadow-sm border-b border-brand-border">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                type="button"
                className="lg:hidden -ml-0.5 -mt-0.5 inline-flex h-11 w-11 items-center justify-center rounded-md text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
                onClick={() => setSidebarOpen(true)}
                aria-label={t('nav.openSidebar')}
              >
                <Menu className="h-5 w-5" strokeWidth={2} />
              </button>

              {header_tagline && (
                <div className="ml-4 lg:ml-0 hidden sm:flex items-center">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-text-muted leading-none whitespace-nowrap">
                    {header_tagline}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <AdminLanguageSwitcher />
              <span aria-hidden className="hidden md:block h-6 w-px bg-brand-border" />
              <div className="hidden md:flex flex-col items-end leading-tight">
                <span className="text-[13px] font-semibold text-brand-text">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="text-[11px] text-brand-text-muted">{user?.email}</span>
              </div>
              <div
                className="h-9 w-9 rounded-full bg-gradient-to-br from-brand-primary to-brand-primary-hover flex items-center justify-center ring-2 ring-brand-accent/40 ring-offset-2 ring-offset-brand-surface shadow-sm"
                aria-hidden
              >
                <span className="text-sm font-semibold text-white">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </span>
              </div>
              <button
                onClick={handleLogout}
                title={t('nav.logout')}
                aria-label={t('nav.logout')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-brand-text-muted hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 dark:hover:text-rose-400 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40"
              >
                <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1 py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>

        <footer className="mt-auto border-t border-brand-border bg-brand-surface/60 backdrop-blur-sm">
          <div className="px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] text-brand-text-muted">
            <span>Powered by <span className="font-semibold tracking-wide text-brand-text">LEANOFY</span></span>
            <span aria-hidden className="text-brand-border">·</span>
            <span>
              {t('nav.support')}:{' '}
              <a
                href="https://www.leanofy.de"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 rounded-sm"
              >
                www.leanofy.de
              </a>
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
};

const AdminLayout: React.FC = () => (
  <ToastProvider>
    <BrandingProvider>
      <AdminLayoutInner />
    </BrandingProvider>
  </ToastProvider>
);

export default AdminLayout;
